// «Файл» of a card (stage J7): a link field takes an uploaded file — the storage keeps it under the record, the field gets the
// platform's address; the team downloads it by the rights to the record.

import { useRef, useState } from 'react';
import type { Id } from '@confdeck/core-shared';
import { useRecord, useSync } from '../sync/react.tsx';
import { Button, useToast } from '../ui/primitives.tsx';
import s from './record.module.css';

/** The name of an uploaded file: the last part of its address is the name in base64url. */
function fileName(url: string): string {
  try { const b = (url.split('/').pop() ?? '').replace(/-/g, '+').replace(/_/g, '/'); return new TextDecoder().decode(Uint8Array.from(atob(b), (c) => c.charCodeAt(0))); } catch { return 'Файл'; }
}

/** A tab component for a link field `field` of the card: «Загрузить файл», the current file and its download. */
export function attachTab(field: string, hint: string) {
  return function AttachFile({ recordId }: { recordId: Id }) {
    const rec = useRecord(recordId);
    const sync = useSync();
    const toast = useToast();
    const input = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const url = String(rec?.data[field] ?? '');
    const own = url.includes('/api/files/records/');
    const upload = async (file: File) => {
      setBusy(true);
      // A card made a moment ago may still be on its way to the server: the upload waits for the outbox, or the server answers
      // «Запись не найдена» (seen on TEST, where the e2e uploaded right after «+ Материал»).
      for (let i = 0; i < 150 && sync.unsent > 0; i++) await new Promise((r) => setTimeout(r, 200));
      if (sync.unsent > 0) { setBusy(false); toast({ kind: 'error', text: 'Карточка ещё не сохранена на сервере — загрузите файл чуть позже' }); return; }
      const res = await fetch(`/api/records/${recordId}/attach?field=${field}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream', 'x-file-name': encodeURIComponent(file.name), 'x-file-type': file.type || 'application/octet-stream' }, body: file });
      const body = await res.json().catch(() => ({})) as { name?: string; error?: { message: string } };
      setBusy(false);
      toast(res.ok ? { kind: 'info', text: `Файл загружен: ${body.name}` } : { kind: 'error', text: body.error?.message ?? 'Загрузить не удалось' });
    };
    return (
      <div className={s.attach}>
        <p className={s.attachHint}>{hint}</p>
        {own && <a className={s.attachLink} href={url.slice(url.indexOf('/api/files/'))}>{fileName(url)}</a>}
        {url && !own && <p className={s.attachHint}>Сейчас — ссылка: <a href={url} target="_blank" rel="noreferrer">{url}</a></p>}
        <input ref={input} type="file" hidden aria-label="Файл" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
        <Button size="sm" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Загружаем…' : own ? 'Заменить файл' : 'Загрузить файл'}</Button>
      </div>
    );
  };
}
